import { api } from './api';

const BASE = '/api/v1/data-explorer';

export interface ExplorerProject {
  _id: string;
  name: string;
  appCode?: string | null;
  /** The partner that owns it. Null is real — a project can stand alone. */
  partnerId?: string | null;
  partnerName?: string | null;
  /** Rows this project holds across every explorable collection. */
  records: number;
  /** How many collections it actually appears in. */
  collections: number;
  /** How many modules it has switched on — the headline a project card wants,
   *  since one module spans many collections. */
  modules: number;
}

export interface ExplorerCollection {
  collection: string;
  label: string;
  /** One line on what the thing IS. The count says how much; this says why. */
  description: string;
  /** What it COUNTS — "prompts", "districts". Cards say "32 prompts" rather
   *  than "32 records", which is the database's word, not anybody else's. */
  unit: string;
  unitOne: string;
  /** The module it belongs to — the same three the platform dashboard uses. */
  module: string;
  moduleLabel: string;
  moduleDescription: string;
  /** The collective noun for everything in the module. */
  moduleUnit: string;
  /** Two labels when the module separates what was set up from what running it
   *  produced — ["Masters", "Executions"] — or null when it is all one kind. */
  moduleSplit: [string, string] | null;
  /** The collection this one hangs under, or null when it stands on its own. */
  master: string | null;
  /** Which side of a split module it belongs to: what was set up, or what
   *  running it produced. Stated by the server rather than guessed here — a
   *  RAG version is not an execution just because it is not a master. */
  half: 'masters' | 'runs';
  /** Colour the UI owns; the server decides which capability wears which, so
   *  the palette is the same wherever a project is rendered. */
  tone: string;
  count: number;
}

export interface ExplorerFilter {
  field: string;
  values: string[];
}

export interface ExplorerRecords {
  items: Record<string, unknown>[];
  /** Which fields are worth a dropdown — computed from the data, not a fixed list. */
  filters: ExplorerFilter[];
  total: number;
}

/** Read-only browse over the project-scoped collections. See the router's own
 *  docstring for what is allow-listed and what is redacted before it ships. */
export const dataExplorerService = {
  async projects(): Promise<ExplorerProject[]> {
    const res = await api.get<{ data: ExplorerProject[] }>(`${BASE}/projects`);
    return res.data.data || [];
  },

  async collections(appId: string): Promise<ExplorerCollection[]> {
    const res = await api.get<{ data: ExplorerCollection[] }>(`${BASE}/collections`, {
      params: { appId },
    });
    return res.data.data || [];
  },

  async records(params: {
    appId: string;
    collection: string;
    search?: string;
    field?: string;
    value?: string;
    page?: number;
    page_size?: number;
  }): Promise<ExplorerRecords> {
    const res = await api.get<{
      data: { items: Record<string, unknown>[]; filters: ExplorerFilter[] };
      pagination?: { totalItems?: number; total?: number };
    }>(`${BASE}/records`, { params });
    const body = res.data?.data;
    const p = res.data?.pagination;
    return {
      items: body?.items ?? [],
      filters: body?.filters ?? [],
      total: p?.totalItems ?? p?.total ?? body?.items?.length ?? 0,
    };
  },
};

export default dataExplorerService;
